import React, { useCallback, useEffect, useMemo } from "react";

import { formatDistanceToNow } from "date-fns";

import useGetAllChatBotSessions from "@fartherfinance/frontend/api/Chatbot/hooks/useGetAllChatBotSessions";
import { ChatBotSessionId } from "@fartherfinance/frontend/api/Chatbot/Types";

import { useChatStore } from "../../reducer/Context";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import DataList from "@src/sharedComponents/DataList/DataList";

import styles from "./ChatSessionList.module.css";

interface ChatSessionProps {
  className?: string;
  height?: number | string;
  isExpanded?: boolean;
}

const PAGE_SIZE = 100;

const ChatSessionList: React.FC<ChatSessionProps> = ({
  isExpanded = false,
}) => {
  const { sessionId } = useChatStore();

  const advisorAuth = useAdvisorRequestAuth();

  const [page, setPage] = React.useState(1);

  const { data: chatBotSessionsResponse, isLoading: isLoadingAllSessions } =
    useGetAllChatBotSessions(
      {
        page,
        pageSize: PAGE_SIZE,
      },
      advisorAuth
    );

  const [chatBotSessions, pagination] = useMemo(() => {
    if (chatBotSessionsResponse === undefined) {
      return [];
    }

    return [
      chatBotSessionsResponse.data.map((session) => ({
        ...session,
        updatedAt: formatDistanceToNow(new Date(session.updatedAt), {
          addSuffix: true,
        }),
      })),
      chatBotSessionsResponse.pagination,
    ];
  }, [chatBotSessionsResponse]);

  const handleOnEndReached = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0].isIntersecting;

      if (
        isIntersecting &&
        !isLoadingAllSessions &&
        pagination?.hasNextPage &&
        (chatBotSessions?.length ?? 0) >= PAGE_SIZE
      ) {
        setPage((prev) => prev + 1);
      }
    },
    [isLoadingAllSessions, pagination, chatBotSessions]
  );

  const { setChatSessionId } = useChatStore();

  const onSessionSelect = (sessionId: ChatBotSessionId | null) => () => {
    if (!sessionId) {
      return;
    }

    setChatSessionId(sessionId);
  };

  useEffect(() => {
    return () => {
      setPage(1);
    };
  }, [sessionId]);

  const dataListHeight = useMemo(() => {
    return isExpanded ? "calc(100vh - 270px)" : 480;
  }, [isExpanded]);

  if (isLoadingAllSessions) {
    return <div>Loading...</div>;
  }

  return (
    <DataList
      className={styles.chatSessionDataList}
      height={dataListHeight}
      onEndReached={handleOnEndReached}
      noResultsText="Start a new conversation"
    >
      {(chatBotSessions ?? []).map((session) => (
        <DataList.DataListItem
          key={session.sessionId}
          title={session.title || "Untitled Conversation"}
          subTitle={session.updatedAt}
          onClick={onSessionSelect(session.sessionId)}
          iconType="caret"
        />
      ))}
    </DataList>
  );
};

export default ChatSessionList;
